import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Phone, Trash2 } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { Eyebrow, MaskedLines, Reveal } from '../components/bits';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'deluxe-admin-token';

const inputCls =
  'w-full border border-white/20 bg-white/5 px-4 py-3.5 text-sm text-white placeholder:text-white/40 transition-colors focus:border-[#C6A15B] focus:outline-none';

function formatApiErrorDetail(detail) {
  if (detail == null) return null;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg).filter(Boolean).join(' ');
  return String(detail);
}

const STATUS_STYLES = {
  new: 'bg-[#C6A15B] text-ink',
  contacted: 'bg-forest text-white',
  completed: 'bg-neutral-200 text-neutral-600',
};

export default function Admin() {
  const { t, lang } = useLanguage();
  const a = t.admin;
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || '');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [signingIn, setSigningIn] = useState(false);
  const [quotes, setQuotes] = useState(null);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    document.title = 'Admin | DELUXE ENTERPRISE';
  }, []);

  const serviceLabel = useCallback(
    (value) => t.form.serviceOptions.find((o) => o.value === value)?.label || value,
    [t]
  );
  const frequencyLabel = useCallback(
    (value) => t.form.frequencyOptions.find((o) => o.value === value)?.label || value,
    [t]
  );
  const statusLabel = useCallback(
    (s) => (s === 'new' ? a.newLabel : s === 'contacted' ? a.contacted : a.completed),
    [a]
  );

  const load = useCallback(
    async (tok) => {
      try {
        const { data } = await axios.get(`${API}/api/quotes`, {
          headers: { Authorization: `Bearer ${tok}` },
        });
        setQuotes(data);
        setLoadError('');
      } catch (e) {
        if (e.response?.status === 401) {
          localStorage.removeItem(TOKEN_KEY);
          setToken('');
        } else {
          setLoadError(a.loadError);
        }
      }
    },
    [a]
  );

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const onLogin = async (e) => {
    e.preventDefault();
    setSigningIn(true);
    setLoginError('');
    try {
      const { data } = await axios.post(`${API}/api/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      setToken(data.access_token);
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) setLoginError(a.locked);
      else setLoginError(formatApiErrorDetail(err.response?.data?.detail) === 'Invalid credentials' ? a.wrong : a.wrong);
    } finally {
      setSigningIn(false);
    }
  };

  const setStatus = async (id, status) => {
    await axios.patch(
      `${API}/api/quotes/${id}`,
      { status },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setQuotes((prev) => prev.map((q) => (q.id === id ? { ...q, status } : q)));
  };

  const remove = async (id) => {
    if (!window.confirm(a.confirmDelete)) return;
    await axios.delete(`${API}/api/quotes/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setQuotes((prev) => prev.filter((q) => q.id !== id));
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken('');
    setQuotes(null);
  };

  if (!token) {
    return (
      <main className="flex min-h-[100svh] items-center justify-center bg-ink px-6 py-32">
        <div className="w-full max-w-md" data-testid="admin-login">
          <Eyebrow dark>DELUXE ENTERPRISE</Eyebrow>
          <MaskedLines
            as="h1"
            lines={[a.loginHeading]}
            className="mt-6 font-display text-4xl font-extrabold uppercase tracking-tight text-white"
          />
          <p className="mt-4 text-sm text-white/50">{a.loginSub}</p>
          <form onSubmit={onLogin} className="mt-10 space-y-5">
            <div>
              <label htmlFor="admin-email" className="mb-2 block font-display text-xs font-bold uppercase tracking-[0.14em] text-white/70">
                {a.email}
              </label>
              <input
                id="admin-email"
                type="email"
                data-testid="admin-email"
                className={inputCls}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                required
              />
            </div>
            <div>
              <label htmlFor="admin-password" className="mb-2 block font-display text-xs font-bold uppercase tracking-[0.14em] text-white/70">
                {a.password}
              </label>
              <input
                id="admin-password"
                type="password"
                data-testid="admin-password"
                className={inputCls}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </div>
            {loginError && (
              <p role="alert" data-testid="admin-login-error" className="border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
                {loginError}
              </p>
            )}
            <button
              type="submit"
              disabled={signingIn}
              data-testid="admin-login-submit"
              className="w-full bg-[#C6A15B] py-4 font-display text-sm font-bold uppercase tracking-[0.18em] text-ink transition-colors hover:bg-[#b8924c] disabled:opacity-60"
            >
              {signingIn ? a.signingIn : a.signIn}
            </button>
          </form>
          <Link to="/" className="mt-8 inline-block text-sm text-white/50 transition-colors hover:text-white" data-testid="admin-back-home">
            {a.backToSite}
          </Link>
        </div>
      </main>
    );
  }

  const counts = {
    total: quotes?.length ?? 0,
    new: quotes?.filter((q) => (q.status || 'new') === 'new').length ?? 0,
    contacted: quotes?.filter((q) => q.status === 'contacted').length ?? 0,
    completed: quotes?.filter((q) => q.status === 'completed').length ?? 0,
  };

  return (
    <main className="min-h-[100svh] bg-cream">
      <section className="bg-ink pb-14 pt-36">
        <div className="mx-auto flex max-w-7xl flex-wrap items-end justify-between gap-6 px-6 sm:px-12 lg:px-20">
          <div>
            <Eyebrow dark>DELUXE ENTERPRISE</Eyebrow>
            <MaskedLines
              as="h1"
              lines={[a.title]}
              className="mt-5 font-display text-4xl font-extrabold uppercase tracking-tight text-white sm:text-5xl"
            />
          </div>
          <button
            type="button"
            onClick={logout}
            data-testid="admin-logout"
            className="border border-white/25 px-5 py-3 font-display text-xs font-bold uppercase tracking-[0.16em] text-white transition-colors hover:bg-white hover:text-ink"
          >
            {a.logout}
          </button>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-12 sm:px-12 lg:px-20" data-testid="admin-dashboard">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {[
            { label: a.total, value: counts.total, id: 'total' },
            { label: a.newLabel, value: counts.new, id: 'new' },
            { label: a.contacted, value: counts.contacted, id: 'contacted' },
            { label: a.completed, value: counts.completed, id: 'completed' },
          ].map((s) => (
            <div key={s.id} className="border border-ink/10 bg-white p-5" data-testid={`admin-stat-${s.id}`}>
              <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-forest">{s.label}</p>
              <p className="mt-2 font-display text-3xl font-extrabold text-ink">{s.value}</p>
            </div>
          ))}
        </div>

        {loadError && (
          <p role="alert" className="mt-8 border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="admin-load-error">
            {loadError}
          </p>
        )}

        {quotes && quotes.length === 0 && (
          <div className="mt-10 border border-dashed border-forest/30 p-14 text-center" data-testid="admin-empty">
            <p className="font-display text-xl font-bold uppercase tracking-tight text-ink">{a.empty}</p>
            <p className="mt-2 text-sm text-neutral-500">{a.emptySub}</p>
          </div>
        )}

        <div className="mt-10 space-y-6">
          {quotes?.map((q, i) => {
            const status = q.status || 'new';
            return (
              <Reveal key={q.id} delay={Math.min(i * 0.05, 0.3)}>
                <article className="border border-ink/10 bg-white p-6 sm:p-8" data-testid={`admin-quote-${q.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <h2 className="font-display text-xl font-extrabold tracking-tight text-ink">
                        {q.first_name} {q.last_name}
                      </h2>
                      <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-500">
                        {a.received}{' '}
                        {new Date(q.created_at).toLocaleString(lang === 'fr' ? 'fr-CA' : 'en-CA', {
                          dateStyle: 'medium',
                          timeStyle: 'short',
                        })}
                      </p>
                    </div>
                    <span
                      className={`px-3 py-1.5 font-display text-[11px] font-bold uppercase tracking-[0.14em] ${STATUS_STYLES[status]}`}
                      data-testid={`admin-status-${q.id}`}
                    >
                      {statusLabel(status)}
                    </span>
                  </div>

                  <div className="mt-5 grid grid-cols-1 gap-3 border-t border-ink/10 pt-5 text-sm text-neutral-700 sm:grid-cols-2">
                    <a href={`tel:${q.phone}`} className="flex items-center gap-2.5 transition-colors hover:text-forest" data-testid={`admin-phone-${q.id}`}>
                      <Phone className="h-4 w-4 text-[#C6A15B]" aria-hidden="true" /> {q.phone}
                    </a>
                    <a href={`mailto:${q.email}`} className="flex items-center gap-2.5 break-all transition-colors hover:text-forest" data-testid={`admin-email-${q.id}`}>
                      <Mail className="h-4 w-4 shrink-0 text-[#C6A15B]" aria-hidden="true" /> {q.email}
                    </a>
                    <p className="flex items-center gap-2.5 sm:col-span-2">
                      <MapPin className="h-4 w-4 shrink-0 text-[#C6A15B]" aria-hidden="true" /> {q.address}
                    </p>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-2">
                    <span className="border border-forest/25 px-3 py-1.5 text-xs font-semibold text-forest" data-testid={`admin-service-${q.id}`}>
                      {serviceLabel(q.service)}
                    </span>
                    <span className="border border-ink/15 px-3 py-1.5 text-xs font-semibold text-neutral-600">
                      {frequencyLabel(q.frequency)}
                    </span>
                  </div>

                  {q.message && (
                    <p className="mt-4 border-l-2 border-[#C6A15B] pl-4 text-sm leading-relaxed text-neutral-600" data-testid={`admin-message-${q.id}`}>
                      {q.message}
                    </p>
                  )}

                  <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-ink/10 pt-5">
                    {status === 'new' && (
                      <button
                        type="button"
                        onClick={() => setStatus(q.id, 'contacted')}
                        data-testid={`admin-mark-contacted-${q.id}`}
                        className="bg-forest px-4 py-2.5 font-display text-[11px] font-bold uppercase tracking-[0.14em] text-white transition-colors hover:bg-forest-dark"
                      >
                        {a.markContacted}
                      </button>
                    )}
                    {status !== 'completed' && (
                      <button
                        type="button"
                        onClick={() => setStatus(q.id, 'completed')}
                        data-testid={`admin-mark-completed-${q.id}`}
                        className="border border-ink/20 px-4 py-2.5 font-display text-[11px] font-bold uppercase tracking-[0.14em] text-ink transition-colors hover:bg-ink hover:text-white"
                      >
                        {a.markCompleted}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => remove(q.id)}
                      data-testid={`admin-delete-${q.id}`}
                      className="ml-auto flex items-center gap-2 px-3 py-2.5 text-[11px] font-bold uppercase tracking-[0.14em] text-red-700 transition-colors hover:text-red-900"
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" /> {a.delete}
                    </button>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
      </section>
    </main>
  );
}
